import { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Button,
  Box,
  TextField,
  Select,
  Stack,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { colors } from '../core/theme/theme';
import { serverTimestamp } from 'firebase/firestore';
import type { ProgramType, Role } from '@altyor-kronos/shared-types';
import { PROGRAM_TYPES } from '@altyor-kronos/shared-types';
import { uploadProjectPhoto } from '../core/firebase/storage';
import { useAuth } from '../core/auth/useAuth';
import { useProjectsHubData } from '../features/projects/useProjectsHubData';
import { useUsersList } from '../features/projects/useUsersList';
import ProjectCard from '../features/projects/ProjectCard';
import ProjectWizard, { type ProjectWizardData } from '../features/projects/ProjectWizard';
import type { ClientFormData } from '../features/admin/ClientWizard';
import { isAssignedToMe } from '../features/projects/validators';
import { setProjectFavourite, setProjectHidden, createClient as createClientMutation } from '../core/data/adminMutations';
import {
  createProject,
  updateProject,
  seedProgramMilestones,
  updateAvantProjet,
  updateMilestoneTargetDate,
} from '../core/data/projectMutations';
import { apMilestoneSeeds } from '../features/projects/apMilestoneMap';
import { useSearchParams } from 'react-router';

const PROJECT_CREATOR_ROLES: Role[] = ['admin', 'direction', 'finance', 'project_manager'];
type ProgramFilter = 'all' | ProgramType;
type SortKey = 'name' | 'code' | 'recent';

export default function ProjectsHub() {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const { projects, clients, loading, error } = useProjectsHubData();

  const canCreate = Boolean(
    user?.roles.some((r) => PROJECT_CREATOR_ROLES.includes(r)),
  );

  const [wizardOpen, setWizardOpen] = useState(false);
  const { users } = useUsersList(wizardOpen);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open wizard when navigated with ?new=1 (e.g. from Hattori palette)
  useEffect(() => {
    if (searchParams.get('new') === '1' && canCreate) {
      setWizardOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, canCreate, setSearchParams]);

  const [search, setSearch] = useState('');
  const [programFilter, setProgramFilter] = useState<ProgramFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [showHidden, setShowHidden] = useState(false);
  const [prefError, setPrefError] = useState<string | null>(null);

  const favourites = new Set(user?.favorites ?? []);
  const hidden = new Set(user?.hiddenProjects ?? []);

  const filtered = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return projects
      .filter(({ id, data }) => {
        if (!showHidden && hidden.has(id)) return false;
        if (programFilter !== 'all' && data.currentProgram !== programFilter) return false;
        if (normalizedSearch === '') return true;
        return (
          data.name.toLowerCase().includes(normalizedSearch) ||
          data.code.toLowerCase().includes(normalizedSearch) ||
          (clients.get(data.clientId)?.name ?? '').toLowerCase().includes(normalizedSearch)
        );
      })
      .sort((a, b) => {
        if (sortKey === 'name') return a.data.name.localeCompare(b.data.name);
        if (sortKey === 'code') return a.data.code.localeCompare(b.data.code);
        // recent
        const aT = a.data.updatedAt?.toMillis?.() ?? 0;
        const bT = b.data.updatedAt?.toMillis?.() ?? 0;
        return bT - aT;
      });
  }, [projects, clients, search, programFilter, sortKey, showHidden, hidden]);

  const mine = useMemo(
    () => (user ? filtered.filter(({ data }) => isAssignedToMe(user, data)) : []),
    [filtered, user],
  );

  const mineIds = useMemo(() => new Set(mine.map(({ id }) => id)), [mine]);

  const allNotMine = useMemo(
    () => filtered.filter(({ id }) => !mineIds.has(id)),
    [filtered, mineIds],
  );

  const favs = useMemo(
    () => filtered.filter(({ id }) => favourites.has(id)),
    [filtered, favourites],
  );

  /**
   * Favourites and hidden projects live on the `users/{uid}` doc, which the auth
   * context fetches ONCE at bootstrap — there is no listener behind it. So the
   * write alone leaves the star/eye showing the old value until a page reload
   * (the bug users worked around with F5). Patch the in-memory profile first so
   * the icon flips at 0 ms, then confirm with the server and roll back if it
   * refuses. `updateUser` is the seam; the global write→refetch signal cannot
   * help here because the profile is not a polled resource.
   */
  const togglePref = async (
    field: 'favorites' | 'hiddenProjects',
    projectId: string,
    add: boolean,
  ) => {
    if (!user) return;
    const before = user[field] ?? [];
    updateUser({ [field]: add ? [...before, projectId] : before.filter((id) => id !== projectId) });
    try {
      const write = field === 'favorites' ? setProjectFavourite : setProjectHidden;
      await write(user.uid, projectId, add);
    } catch (e) {
      updateUser({ [field]: before }); // server said no — put the icon back
      setPrefError(e instanceof Error ? e.message : String(e));
    }
  };

  const toggleFavourite = (projectId: string) =>
    togglePref('favorites', projectId, !favourites.has(projectId));

  const toggleHidden = (projectId: string) =>
    togglePref('hiddenProjects', projectId, !hidden.has(projectId));

  const createClient = async (values: ClientFormData): Promise<string> => {
    return createClientMutation({
      name: values.name.trim(),
      description: values.description?.trim() || null,
      logoURL: values.logoURL?.trim() || null,
      website: values.website?.trim() || null,
      linkedinURL: values.linkedinURL?.trim() || null,
      industry: values.industry?.trim() || null,
      companyType: values.companyType || null,
      employeeRange: values.employeeRange || null,
      country: values.country?.trim() || null,
      city: values.city?.trim() || null,
      foundedYear: values.foundedYear ? parseInt(values.foundedYear, 10) : null,
      parentCompany: values.parentCompany?.trim() || null,
      tags: values.tags?.length ? values.tags : null,
      // adminMutations.createClient only stamps `createdAt` — the inline
      // write this replaces also stamped `updatedAt`, so it's included here
      // in the caller-built payload to preserve that behaviour.
      updatedAt: serverTimestamp(),
    });
  };

  const submitProject = async (data: ProjectWizardData) => {
    if (!user) return;

    // Create the project first so we have its id for the Storage path; the
    // photo URL is written via a follow-up updateDoc. This ordering means a
    // failed upload leaves the project without a photo, which is preferable
    // to orphan files in Storage.
    const projectId = await createProject({
      code: data.code,
      name: data.name.trim(),
      clientId: data.clientId,
      program: data.program,
      team: data.team,
      links: data.links,
      createdBy: user.uid,
    });

    if (data.photo) {
      const { downloadURL } = await uploadProjectPhoto(projectId, data.photo);
      // updateProject (SDK) already stamps updatedAt; the server stamps it under REST.
      await updateProject(projectId, { photos: [downloadURL] });
    }

    // Jalons du programme choisi (gabarit PAD) — écran 1 de la maquette.
    const milestoneIds = await seedProgramMilestones(projectId, data.program);

    // Avant-projet : le bloc est écrit tel quel, puis ses dates de référence
    // amorcent la cible des jalons correspondants. L'ordre compte — les jalons
    // doivent exister avant qu'on leur pose une date, et `milestoneIds` est la
    // seule façon de retrouver le document d'un `templateId` (les ids sont
    // auto-générés). Un templateId absent du gabarit du programme choisi est
    // simplement ignoré : ScaleUp seul porte ce vocabulaire.
    if (data.avantProjet) {
      await updateAvantProjet(projectId, data.avantProjet);
      for (const seed of apMilestoneSeeds(data.avantProjet)) {
        const milestoneId = milestoneIds[seed.templateId];
        if (!milestoneId) continue;
        await updateMilestoneTargetDate(
          projectId,
          milestoneId,
          seed.date.toDate().toISOString().split('T')[0],
        );
      }
    }
  };

  return (
    <Box>
      {/* Hero header with background image */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mx: { xs: -2, sm: -3, md: -5 },
          mt: { xs: -2, sm: -3, md: -5 },
          mb: 3,
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 3, sm: 4 },
          minHeight: 140,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <Box
          component="img"
          src="/images/projects-header.webp"
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 30%',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(20, 23, 28, 0.55)',
          }}
        />
        <Stack spacing={0.5} sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ color: colors.white, fontWeight: 700 }}>
            {t('projects.hub.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {t('projects.hub.subtitle')}
          </Typography>
        </Stack>
      </Box>

      <Stack
        direction="row"
        useFlexGap
        sx={{ mb: 3, alignItems: 'flex-end', flexWrap: 'wrap', gap: 1, rowGap: 1.5 }}
      >
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('projects.hub.searchPlaceholder')}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          size="small"
          sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: 130 }, maxWidth: { xs: '100%', sm: 220 }, '& .MuiInputBase-root': { height: 40 } }}
        />
        <Stack direction="column" spacing={0.25} sx={{ flexShrink: 0, width: 130 }}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, textAlign: 'center', display: 'block' }}>
            {t('projects.hub.programFilter')}
          </Typography>
          <Select
            size="small"
            value={programFilter}
            onChange={(e) => setProgramFilter(e.target.value as ProgramFilter)}
            sx={{ width: '100%', height: 40 }}
          >
            <MenuItem value="all">{t('projects.hub.programAll')}</MenuItem>
            {PROGRAM_TYPES.map((p) => (
              <MenuItem key={p} value={p}>
                {t(`projects.program.${p}`)}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Stack direction="column" spacing={0.25} sx={{ flexShrink: 0, width: 120 }}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, textAlign: 'center', display: 'block' }}>
            {t('projects.hub.sortLabel')}
          </Typography>
          <Select
            size="small"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            sx={{ width: '100%', height: 40 }}
          >
            <MenuItem value="name">{t('projects.hub.sortByName')}</MenuItem>
            <MenuItem value="code">{t('projects.hub.sortByCode')}</MenuItem>
            <MenuItem value="recent">{t('projects.hub.sortByRecent')}</MenuItem>
          </Select>
        </Stack>
        <FormControlLabel
          control={<Switch checked={showHidden} onChange={(_, v) => setShowHidden(v)} size="small" />}
          label={t('projects.hub.hiddenLabel')}
          sx={{ ml: 0, flexShrink: 0, whiteSpace: 'nowrap' }}
        />
        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
        {canCreate && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setWizardOpen(true)}
            sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
          >
            {t('projects.newProject')}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      {prefError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPrefError(null)}>
          {prefError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Section title={t('projects.favorites')} emptyText={t('projects.hub.emptyFavorites')}>
            {favs.map(({ id, data }) => (
              <ProjectCard
                key={id}
                project={{ ...data, id }}
                clientName={clients.get(data.clientId)?.name}
                favourited={favourites.has(id)}
                hidden={hidden.has(id)}
                onToggleFavourite={toggleFavourite}
                onToggleHidden={toggleHidden}
              />
            ))}
          </Section>

          <Section title={t('projects.myProjects')} emptyText={t('projects.hub.emptyMine')}>
            {mine.map(({ id, data }) => (
              <ProjectCard
                key={id}
                project={{ ...data, id }}
                clientName={clients.get(data.clientId)?.name}
                favourited={favourites.has(id)}
                hidden={hidden.has(id)}
                onToggleFavourite={toggleFavourite}
                onToggleHidden={toggleHidden}
              />
            ))}
          </Section>

          <Section title={t('projects.allProjects')} emptyText={t('projects.hub.emptyAll')}>
            {allNotMine.map(({ id, data }) => (
              <ProjectCard
                key={id}
                project={{ ...data, id }}
                clientName={clients.get(data.clientId)?.name}
                favourited={favourites.has(id)}
                hidden={hidden.has(id)}
                onToggleFavourite={toggleFavourite}
                onToggleHidden={toggleHidden}
              />
            ))}
          </Section>

        </>
      )}

      <ProjectWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSubmit={submitProject}
        existingProjects={projects.map(({ id, data }) => ({ id, code: data.code }))}
        clients={Array.from(clients.entries()).map(([id, data]) => ({ id, data }))}
        users={users}
        onCreateClient={createClient}
      />
    </Box>
  );
}

// 4 cards = exactly one row on lg (4-column grid) — shows a clean first glance
// without overwhelming the user; "View more" reveals the rest.
const SECTION_PAGE_SIZE = 8;

function Section({
  title,
  emptyText,
  children,
  initialCollapsed = false,
}: {
  title: string;
  emptyText: string;
  children: React.ReactNode;
  initialCollapsed?: boolean;
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [showAll, setShowAll] = useState(false);
  const items = Array.isArray(children) ? (children as React.ReactNode[]) : [];
  const count = items.length;
  const truncated = !showAll && count > SECTION_PAGE_SIZE;
  const visible = truncated ? items.slice(0, SECTION_PAGE_SIZE) : items;

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: collapsed ? 0 : 2 }}>
        <IconButton
          size="small"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? 'expand' : 'collapse'}
          sx={{ color: colors.textMuted }}
        >
          {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
        <Typography variant="h3">{title}</Typography>
        {count > 0 && (
          <Typography variant="body2" color="text.secondary">
            ({count})
          </Typography>
        )}
      </Box>
      {!collapsed && (
        count === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ pl: 5 }}>
            {emptyText}
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
              }}
            >
              {visible}
            </Box>
            {count > SECTION_PAGE_SIZE && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowAll((v) => !v)}
                >
                  {showAll ? t('common.viewLess') : t('common.viewMore')}
                </Button>
              </Box>
            )}
          </>
        )
      )}
    </Box>
  );
}
